import React, { useCallback, useEffect, useState } from 'react';
import { FlightDetailsDialog, fetchFlightDetails, type Flight } from '../../flight-details';
import { formatDateISO } from '../../../utils/dateUtils';
import type { FileItem } from '../types';

interface FileFlightDetailsDialogProps {
  /** The file whose linked flight to show; `null` keeps the dialog closed. */
  file: FileItem | null;
  onClose: () => void;
  /** Invoked after a successful metadata edit so the caller can refresh its list. */
  onFlightUpdated?: () => void;
}

/**
 * Bridges a Files-tab file to the shared editable Flight Details dialog.
 *
 * A file carries the flight's UUID (`flight_id`) plus `serial_number` and
 * `flight_date`. We reuse the existing by-date fetch and filter to the single
 * flight the file represents — the same pattern the Overview "Latest Flights"
 * list uses — so no dedicated single-flight endpoint is needed.
 */
export const FileFlightDetailsDialog: React.FC<FileFlightDetailsDialogProps> = ({
  file,
  onClose,
  onFlightUpdated,
}) => {
  const [flights, setFlights] = useState<Flight[]>([]);
  const [loading, setLoading] = useState(false);

  // Mirror loadDetails' guard: a file needs all three to fetch + scope a flight.
  // (The backend enrichment sets them together, but stay defensive.)
  const open = file != null && !!file.flight_id && !!file.serial_number && !!file.flight_date;
  const droneSerial = file?.serial_number ?? '';
  const flightDate = file?.flight_date ? formatDateISO(file.flight_date) : null;

  const loadDetails = useCallback(async () => {
    if (!file?.flight_id || !file.serial_number || !file.flight_date) {
      setFlights([]);
      return;
    }
    setLoading(true);
    try {
      const response = await fetchFlightDetails(
        file.serial_number,
        formatDateISO(file.flight_date)
      );
      // Scope to the single flight this file represents (a date can hold several).
      setFlights(
        response.success ? response.flights.filter((f) => f.flight_id === file.flight_id) : []
      );
    } catch (err) {
      // Log only the message — a raw AxiosError carries request config incl. the JWT.
      const message = err instanceof Error ? err.message : String(err);
      console.error('Error fetching flight details:', message);
      setFlights([]);
    } finally {
      setLoading(false);
    }
  }, [file]);

  useEffect(() => {
    if (open) {
      void loadDetails();
    } else {
      setFlights([]);
    }
  }, [open, loadDetails]);

  const handleFlightUpdated = async () => {
    await loadDetails(); // refresh the dialog's own view with the saved values
    onFlightUpdated?.(); // let the Files tab refresh its list
  };

  return (
    <FlightDetailsDialog
      open={open}
      onClose={onClose}
      flightDate={flightDate}
      droneSerial={droneSerial}
      flights={flights}
      loading={loading}
      onFlightUpdated={handleFlightUpdated}
    />
  );
};
